import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import './MfaCodeInput.css';

interface MfaCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  onComplete?: (code: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  // Surfaced to screen readers; defaults to "Verification code"
  ariaLabel?: string;
}

/**
 * 6-digit verification code input. Auto-advances per digit, supports
 * paste of a full code, and handles backspace by stepping back.
 *
 * Treat the value as the source of truth — render based on value.split('').
 * Don't keep per-cell state in this component.
 */
export function MfaCodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  autoFocus = false,
  disabled = false,
  ariaLabel = 'Verification code',
}: MfaCodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  const setDigit = (index: number, digit: string) => {
    if (disabled) return;
    const sanitized = digit.replace(/\D/g, '').slice(0, 1);
    const chars = value.padEnd(length, ' ').split('');
    chars[index] = sanitized || ' ';
    const next = chars.join('').replace(/ /g, '').slice(0, length);
    onChange(next);

    if (sanitized && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (next.length === length) {
      onComplete?.(next);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      e.preventDefault();
      const chars = value.split('');
      chars.splice(index - 1, 1);
      onChange(chars.join(''));
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    if (pasted.length === length) {
      onComplete?.(pasted);
      inputsRef.current[length - 1]?.focus();
    } else {
      inputsRef.current[pasted.length]?.focus();
    }
  };

  return (
    <div
      className="mfa-code-input"
      role="group"
      aria-label={ariaLabel}
      data-testid="mfa-code-input"
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={value[i] ?? ''}
          aria-label={`Digit ${i + 1}`}
          onChange={e => setDigit(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          className="mfa-code-input__cell"
        />
      ))}
    </div>
  );
}
